"use client";

import { createDisplay } from "@/app/(users)/[locationId]/(dashboard)/(store)/displays/actions/create-display";
import { getDisplay } from "@/app/(users)/[locationId]/(dashboard)/(store)/displays/actions/get-display";
import { getDisplays } from "@/app/(users)/[locationId]/(dashboard)/(store)/displays/actions/get-displays";
import { updateDisplay } from "@/app/(users)/[locationId]/(dashboard)/(store)/displays/actions/update-display";
import { supabase } from "@/lib/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

export const useDisplay = (
  displayId?: string,
  {
    listenDisplayEvents,
  }: {
    listenDisplayEvents: boolean;
  } = {
    listenDisplayEvents: false,
  },
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (listenDisplayEvents && displayId) {
      const channel = supabase.channel("displays").on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "displays",
          filter: `id=eq.${displayId}`,
        },
        (payload) => {
          queryClient.invalidateQueries({
            queryKey: ["displays", displayId],
          });
        },
      );

      channel.subscribe();

      return () => {
        channel.unsubscribe();
      };
    }
  });

  return useQuery({
    queryKey: ["displays", displayId],
    queryFn: () => getDisplay({ displayId: displayId! }),
    enabled: !!displayId,
  });
};

export const useCreateDisplay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDisplay,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["displays"],
      });
    },
  });
};

export const useUpdateDisplay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDisplay,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["displays"] });
    },
  });
};

export const useDisplays = (locationId?: string) => {
  return useQuery({
    queryKey: ["displays", locationId],
    queryFn: () => getDisplays({ locationId: locationId! }),
    enabled: !!locationId,
  });
};
